import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
} from '@mui/material';
import { EmojiEvents as TrophyIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { useCriteriaOptions } from 'services/Data/CriteriaOptionServices';
import { useSubmission, useUpdateSubmission } from 'services/Data/SubmissionServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import { getOptionColor } from 'services/Utilities/criteriaOptionUtils';
import { CertificationScoreEnum } from 'enums/CertificationScoreEnum';
import { createSubmissionPatchDocument } from 'services/Serializers/SubmissionPatchDocument';
import { useCriteria } from 'services/Data/CriteriaServices';

const CriteriaOptionSelectionStep = ({ isReadOnly = false }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { submissionId } = useParams();
  const currentLanguage = i18n.language;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { data: submission } = useSubmission(submissionId);
  const { data: criteriaOptions, isLoading, error } = useCriteriaOptions();
  const { data: criteria } = useCriteria(submission?.criteria_Id);
  const updateSubmission = useUpdateSubmission(submissionId);

  const heroUnitProductLineId = submission?.heroUnitProductLine_Id || submission?.heroUnitProductLine?.id || null;
  const dealerProductLineIds = useMemo(
    () => submission?.dealer?.dealerProductLines?.map(line => line.id).filter(Boolean) || [],
    [submission?.dealer?.dealerProductLines],
  );

  const submissionCriteriaId = submission?.criteria_Id || null;

  const availableOptions = useMemo(() => {
    if (!criteriaOptions) return [];
    return criteriaOptions
      .filter(option => {
        if (!option || option.isAvailableForDealerSelection !== true) return false;
        if (!submissionCriteriaId) return true;

        const matchesJoin = (option.criteriaCriteriaOptions || []).some(join => join?.criteria_Id === submissionCriteriaId);
        const matchesLegacy = option.criteria_Id === submissionCriteriaId;
        return matchesJoin || matchesLegacy;
      })
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  }, [criteriaOptions, submissionCriteriaId]);
  const filterCriteriaTypesForOption = (criteriaTypes, optionId) => {
    if (!criteriaTypes || !Array.isArray(criteriaTypes) || !dealerProductLineIds.length) {
      return [];
    }

    if (!heroUnitProductLineId && criteria?.isHeroUnitVisible) return [];

    return criteriaTypes
      .filter(criteriaType => {
        if (!criteriaType) return false;

        if (submission?.criteria_Id && criteriaType.criteria_Id && criteriaType.criteria_Id !== submission.criteria_Id) {
          return false;
        }

        const optionIds =
          criteriaType.criteriaTypeCriteriaOptions?.map(join => join?.criteriaOption_Id).filter(Boolean) || [];
        if (optionIds.length && (!optionId || !optionIds.includes(optionId))) return false;

        const productLineIds =
          criteriaType.criteriaTypeProductLines?.map(join => join?.productLine_Id).filter(Boolean) || [];
        if (criteria?.isHeroUnitVisible && !productLineIds.includes(heroUnitProductLineId)) return false;

        const dealerLineIds =
          criteriaType.criteriaTypeDealerProductLines?.map(join => join?.productLine_Id).filter(Boolean) || [];
        if (!dealerLineIds.some(lineId => dealerProductLineIds.includes(lineId))) return false;

        return true;
      })
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  };

  const handleOptionSelected = async optionId => {
    if (isReadOnly) return;
    try {
      const patchDoc = createSubmissionPatchDocument({ attemptedCriteriaOption_Id: optionId });
      await updateSubmission.mutateAsync(patchDoc);
      history.push(`/dealer/certification/submissions/${submissionId}/step/3`);
    } catch (err) {
      console.error('Error selecting criteria option:', err);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress sx={theme => ({ color: theme.palette.primary.main })} />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {t('submission.loadingFailed')} {error.message}
      </Alert>
    );
  }

  const formatOptionName = name => (typeof name === 'string' ? name.toUpperCase().replace(/\s+/g, '_') : '');
  return (
    <Box>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h1" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('criteriaOption.selectYourLevel')}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {t('criteriaOption.selectDescription')}
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {availableOptions.map(option => {
          const formattedName = formatOptionName(option.name.text);
          const score = option.score;
          const optionName = `${translateLabel(option.name, currentLanguage)} - ${score}`;
          const optionDescription = t('submissions.optionDescription', {
            optionName: translateLabel(option.name, currentLanguage),
            criteriaCount: option.criteria?.length,
          });
          const isSelected = submission?.attemptedCriteriaOption_Id === option.id;
          const color = getOptionColor(optionName);
          const cardBg = isDark ? '#1c1c1c' : '#ffffff';
          const cardBorder = isSelected ? color : isDark ? 'rgba(193,193,193,0.25)' : theme.palette.divider;
          const bodyText = isDark ? 'rgba(255,255,255,0.78)' : theme.palette.text.primary;
          const mutedText = isDark ? 'rgba(255,255,255,0.65)' : theme.palette.text.secondary;
          const surfaceShadow = isSelected
            ? `0 0 20px ${color}40`
            : isDark
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : theme.shadows[2];

          return (
            <Grid item xs={12} md={4} key={option.id} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  bgcolor: cardBg,
                  border: isSelected ? `2px solid ${color}` : `1px solid ${cardBorder}`,
                  borderRadius: 2,
                  boxShadow: surfaceShadow,
                  transition: 'all 0.3s ease',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 20px ${color}40`,
                    border: `2px solid ${color}`,
                  },
                }}>
                <CardActionArea
                  disabled={isReadOnly}
                  onClick={() => handleOptionSelected(option.id)}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 3,
                    cursor: isReadOnly ? 'not-allowed' : 'pointer',
                    opacity: isReadOnly ? 0.75 : 1,
                  }}>
                  {isSelected && (
                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                      <CheckIcon sx={{ color, fontSize: 32 }} />
                    </Box>
                  )}

                  <CardContent sx={{ textAlign: 'center', p: 0, height: '100%' }}>
                    <Box sx={{ mb: 2, color }}>{<TrophyIcon sx={{ fontSize: 72 }} />}</Box>

                    <Typography variant="h2" sx={{ color, fontWeight: 700, mb: 4, textTransform: 'uppercase' }}>
                      {optionName}
                    </Typography>

                    <Typography variant="body2" sx={{ color: bodyText, lineHeight: 1.5 }}>
                      {optionDescription}
                    </Typography>

                    <Divider sx={{ my: 4, borderColor: isDark ? 'rgba(255,255,255,0.12)' : undefined }} />

                    <Box
                      sx={{
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        mt: 1,
                      }}>
                      {filterCriteriaTypesForOption(option.criteriaTypes, option.id).map((criteriaType, idx) => (
                        <Typography
                          key={criteriaType.id ?? `${criteriaType.name?.text}-${idx}`}
                          variant="body2"
                          sx={{ color: 'white', display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                          <Box
                            component="span"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.08)',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              fontWeight: 700,
                              minWidth: 28,
                              textAlign: 'center',
                              mt: '1px',
                            }}>
                            {idx + 1}
                          </Box>
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography component="span" sx={{ color: bodyText }}>
                              {translateLabel(criteriaType.name, currentLanguage)}
                            </Typography>
                            {!!criteriaType.description && (
                              <Typography component="span" variant="caption" sx={{ color: mutedText }}>
                                {translateLabel(criteriaType.description, currentLanguage)}
                              </Typography>
                            )}
                          </Box>
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CriteriaOptionSelectionStep;
